import type { UpcomingAppointment } from '@web-app/server/services/appointments/types';
import { trpc } from '@web-app/utils/trpc';

export type AppointmentStatus =
  | { type: 'loading' }
  | { type: 'error'; error: Error }
  | { type: 'no-appointment' }
  | { type: 'scheduled'; appointment: UpcomingAppointment };

interface UseUpcomingAppointmentsOptions {
  /**
   * Appointment ID to exclude from the upcoming list (e.g. the current
   * in-session appointment — the user doesn't need to see the appointment
   * they're currently in).
   */
  excludeAppointmentId?: string | null;
}

/**
 * Fetches upcoming appointments for a patient and derives an appointment
 * status for display in status cards.
 *
 * Replaces the appointment status concern that previously lived in
 * appointmentSchedulingMolecule. Works from any page — no Bunshi scope required.
 */
export function useUpcomingAppointments(
  patientId: string | null,
  options?: UseUpcomingAppointmentsOptions,
) {
  const { excludeAppointmentId } = options ?? {};

  const query = trpc.scheduling.getUpcomingForPatient.useQuery(
    { patientId: patientId ?? '' },
    {
      enabled: !!patientId,
      staleTime: 2 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );

  let appointmentStatus: AppointmentStatus;

  if (query.isPending && !query.data) {
    appointmentStatus = { type: 'loading' };
  } else if (query.error) {
    appointmentStatus = {
      type: 'error',
      error:
        query.error instanceof Error
          ? query.error
          : new Error(String(query.error)),
    };
  } else {
    const appointments = (query.data ?? []).filter(
      (appt) => appt.id !== excludeAppointmentId,
    );
    appointmentStatus =
      appointments.length === 0
        ? { type: 'no-appointment' }
        : { type: 'scheduled', appointment: appointments[0] };
  }

  return {
    appointmentStatus,
    isLoading: query.isPending && !query.data,
    error: query.error
      ? query.error instanceof Error
        ? query.error
        : new Error(String(query.error))
      : null,
    refetch: query.refetch,
  };
}
